import { Theme, css } from '@emotion/react';

import { GetServerSideProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';

import { Spacing } from 'chatie-design-system';

import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';

import { loadTermsPageProps } from '@base/lib/terms';
import type { TermsPageProps, TermFile } from '@base/lib/terms';
import { withAuth } from '@contexts/auth';

const containerCSS = (theme: Theme) => css`
  gap: ${Spacing.large}px;
  padding: ${Spacing.large}px ${Spacing.medium}px;
  color: ${theme.var.black};

  h2 {
    margin-top: ${Spacing.xlarge}px;
    margin-bottom: ${Spacing.xlarge}px;
  }

  h3 {
    margin: 32px 0 ${Spacing.medium}px;
  }

  div {
    padding-left: ${Spacing.small}px;
  }

  p {
    margin-bottom: ${Spacing.xsmall}px;
  }

  strong {
    display: block;
    margin-bottom: ${Spacing.medium}px;
  }

  p + ul {
    margin-left: 36px;
  }

  ul {
    list-style-position: outside;
    margin-left: ${Spacing.large}px;
  }

  ul,
  ol,
  li {
    list-style: inherit;
    margin-bottom: ${Spacing.medium}px;
  }

  ol {
    list-style-position: outside;
    list-style-type: decimal;
    margin-left: ${Spacing.large}px;
  }

  li + li {
    margin-top: ${Spacing.xxsmall}px;
  }

  li ul,
  li ol {
    margin-top: ${Spacing.xxsmall}px;
    margin-bottom: 0;
  }

  li ul {
    list-style-type: circle;
  }

  li ol {
    list-style-type: decimal;
  }

  a {
    color: ${theme.colors.main300};
    text-decoration: underline;
  }

  table {
    margin: ${Spacing.xxlarge}px 0 ${Spacing.xsmall}px;
  }

  table,
  table th,
  table td {
    border: 1px solid black;
    border-collapse: collapse;
    padding: ${Spacing.xsmall}px;
  }

  table td {
    vertical-align: middle;
  }
`;

const TERM_TITLES: Record<TermFile, string> = {
  terms: '서비스 이용 약관',
  privacy: '개인정보 처리방침',
};

const TermsPage: NextPage<TermsPageProps> = ({
  termsOfServiceHTML,
  fileName,
}) => {
  const { t } = useTranslation();

  return (
    <Layout>
      <Header title={t(TERM_TITLES[fileName])} />
      <div
        css={containerCSS}
        dangerouslySetInnerHTML={{ __html: termsOfServiceHTML }}
      ></div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps<TermsPageProps> = async ({
  params,
  query,
}) => {
  const props = loadTermsPageProps(params?.file, query?.version);
  return props ? { props } : { notFound: true };
};

export default withAuth(TermsPage, { isSignInRequired: false });
